import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'antd'
import classNames from 'classnames'

import UserService from 'api/Users'
import { isGlobal } from 'conf'
import { QUOTA_MODE } from 'types/utils'
import { isCompanyStorageGaugeVisible } from 'utils/plan'
import { getQuotaStatus, QUOTA_LEVEL } from './quotaStatus'
import styles from './style.module.scss'

const getTooltipKey = (level, max) => {
  if (level !== QUOTA_LEVEL.FULL) return 'record.quota.tooltipUsage'
  // full 이라도 max=0 은 "미할당"으로 안내 문구가 다르다 (인터뷰 확정 — 두 상태가 같은 level 로 수렴)
  return max > 0 ? 'record.quota.tooltipFull' : 'record.quota.tooltipNotAllocated'
}

// byte → 사람이 읽는 단위. 상한이 GB 단위 정책값(10GB/30GB/…)이라 소수 1자리면 충분하다.
const formatBytes = (bytes) => {
  const n = Number(bytes) || 0
  const GB = 1024 ** 3
  const MB = 1024 ** 2
  if (n >= GB) return `${(n / GB).toFixed(1).replace(/\.0$/, '')}GB`
  return `${Math.ceil(n / MB)}MB`
}

// 게이지 한 줄(라벨 + 카운트 + 트랙) — 여러 지표가 한 컨테이너(quotaCard) 안에 섹션으로 쌓인다
const QuotaSection = ({ tooltip, label, count, percent, level }) => (
  <Tooltip title={tooltip} placement="right">
    <div className={classNames(styles.quotaSection, styles[level])}>
      <div className={styles.quotaHeader}>
        <span className={styles.quotaLabel}>{label}</span>
        <span className={styles.quotaCount}>{count}</span>
      </div>
      <div className={styles.quotaTrack}>
        {/* 사용량 0 은 채움 요소를 아예 렌더하지 않는다 — 렌더하면 min-width(알약 최소폭)
            때문에 빈 상태에도 막대가 남는다. 0 초과의 아주 작은 값(예: 10GB 중 16MB)은
            min-width 가 점처럼 찍히는 것을 막아준다. */}
        {percent > 0 && <div className={styles.quotaFill} style={{ width: `${percent}%` }} />}
      </div>
    </div>
  </Tooltip>
)

const ResourceQuotaCard = () => {
  const { t } = useTranslation()
  const paidLicense = useSelector((state) => state.user.paidLicense)
  // menu/RELOAD → GET_DATA 가 매번 새 배열 참조로 SET_STATE — 레코드 생성/삭제 후 재조회 트리거
  const menuData = useSelector((state) => state.menu.menuData)
  const [userQuota, setUserQuota] = useState(null)
  // 연속 RELOAD 시 응답 역전 방지 — 최신 요청의 응답만 반영
  const requestIdRef = useRef(0)

  const enabled = isGlobal() && paidLicense?.quota_mode === QUOTA_MODE.RESOURCE

  useEffect(() => {
    if (!enabled) return
    requestIdRef.current += 1
    const requestId = requestIdRef.current
    UserService.getMyResourceQuota()
      .then((res) => {
        if (requestIdRef.current !== requestId) return
        setUserQuota(res.data?.user_quota ?? null)
      })
      .catch((error) => {
        if (requestIdRef.current !== requestId) return
        // 조용한 실패 — 보조 정보라 사용자 알림 없이 카드만 숨긴다 (설계 확정)
        console.error('Error fetching resource quota:', error)
        setUserQuota(null)
      })
    // paidLicense 참조 포함: SELECT_WORKSPACE 는 menu/RELOAD 없이 라이선스만 재로드하므로
    // (redux/user/sagas.js SELECT_WORKSPACE), 워크스페이스 전환 시 stale quota 방지
  }, [enabled, paidLicense, menuData])

  const sections = []

  if (enabled && userQuota != null) {
    // getQuotaStatus 와 동일한 coercion — 비정상 응답이어도 "undefined / undefined" 노출 방지
    const current = Number(userQuota.current_resources) || 0
    const max = Number(userQuota.max_resources) || 0
    const { percent, level } = getQuotaStatus(current, max)
    sections.push(
      <QuotaSection
        key="resources"
        tooltip={t(getTooltipKey(level, max), { current, max })}
        label={t('record.quota.myResources')}
        count={`${current} / ${max}`}
        percent={percent}
        level={level}
      />,
    )
  }

  // 저장용량 (JP 요금제 §4.4) — license 응답의 산출값만 소비(별도 API 없음).
  // 두 모드는 서버가 상호 배타로 내려준다 (산식 정본은 서버, 2026-08-25):
  //  - 회사 풀(storage_quota_bytes): A축 전체 + B축 Basic — "저장 용량"
  //  - 개인당(personal_storage_quota_bytes): B축 PRO — "내 저장 용량" (1인당 2GB, 사용량도 본인 것)
  // 필드 존재가 곧 노출 게이트(플랜 총량 적용 환경에서만 내려옴). 초과 상태(다운그레이드/
  // 축 전환 직후)는 FULL 색으로 "왜 업로드가 안 되는지"를 상시 보여준다 — 기존 데이터는
  // 소급 차단하지 않고 신규 업로드만 막히는 상태(§4.7).
  const personalQuota = Number(paidLicense?.personal_storage_quota_bytes) || 0
  const isPersonalStorage = personalQuota > 0 && paidLicense?.personal_storage_used_bytes != null
  // 회사 풀은 게이지 노출 판정을 한 번 더 통과해야 한다 — A축 PRO 는 감춘다(사용자 확정 2026-08-25)
  const showCompanyStorage = !isPersonalStorage && isCompanyStorageGaugeVisible(paidLicense)
  const storageQuota = isPersonalStorage
    ? personalQuota
    : Number(paidLicense?.storage_quota_bytes) || 0
  const storageUsedRaw = isPersonalStorage
    ? paidLicense.personal_storage_used_bytes
    : paidLicense?.storage_used_bytes
  if ((isPersonalStorage || showCompanyStorage) && storageQuota > 0 && storageUsedRaw != null) {
    const used = Number(storageUsedRaw) || 0
    const { percent, level } = getQuotaStatus(used, storageQuota)
    const usedLabel = formatBytes(used)
    const quotaLabel = formatBytes(storageQuota)
    sections.push(
      <QuotaSection
        key="storage"
        tooltip={t(
          isPersonalStorage ? 'record.quota.tooltipMyStorage' : 'record.quota.tooltipStorage',
          { used: usedLabel, quota: quotaLabel },
        )}
        label={t(isPersonalStorage ? 'record.quota.myStorage' : 'record.quota.storage')}
        count={`${usedLabel} / ${quotaLabel}`}
        percent={percent}
        level={level}
      />,
    )
  }

  if (sections.length === 0) return null

  // 지표가 몇 개든 하나의 컨테이너(카드)에 섹션으로 쌓는다 (사용자 확정 2026-08-25)
  return <div className={styles.quotaCard}>{sections}</div>
}

export default ResourceQuotaCard
